import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IcArrowRight } from '@healingpaper/cell-icons'

import { CommentCard } from './comment-card'
import { DocumentCard } from './document-card'
import { ShowAllCard } from './show-all-card'

import { NONMEMBER_ID } from '@/common/constants'
import { postAmplitude } from '@/core/lib'
import { DoctorCommunityActivityFilter } from '@/features/community/enums'
import { createSignInUrl } from '@/shared/utils/url-utils'
import { useLocales } from '@/shared/hooks/useLocales'
import { CommunityOfDoctor, DoctorInfo } from '@/features/doctors/[doctorId]/helpers/types/doctor'
import { DoctorDetailAmplitudeType } from '@/features/doctors/[doctorId]/index/helpers/types/event-types'

interface Props {
  community: CommunityOfDoctor
  doctorInfo: DoctorInfo
  userId: number
}

export const ActivitiesInCommunitySection = ({ community, doctorInfo, userId }: Props) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { locale } = useLocales()

  const [communityFilter, setCommunityFilter] = useState<DoctorCommunityActivityFilter>(
    DoctorCommunityActivityFilter.REPLY,
  )

  const MAXIMUM_COMMUNITY_ACTIVITIES_COUNT = 5

  const communityCardsWrapperRef = useRef<HTMLDivElement>(null)
  const [isCommunityDragging, setIsCommunityDragging] = useState(false)

  const [commonAmplitudeProps, setCommonAmplitudeProps] = useState<DoctorDetailAmplitudeType>({
    doctor_id: doctorInfo.id,
    doctor_name: doctorInfo.name,
    hospital_id: doctorInfo.hospitalId,
    hospital_name: doctorInfo.hospitalName,
    referrer: '',
  })

  const handleCommunityCardsWrapperMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isCommunityDragging) {
      const communityCardsWrapper = communityCardsWrapperRef.current

      if (communityCardsWrapper) {
        communityCardsWrapper.scrollLeft -= event.movementX
      }
    }
  }

  const onClickLoginAndWatch = (index: 'comment' | 'document', count: number) => {
    postAmplitude('click_login_doctor_activity_in_doctor_detail', {
      ...commonAmplitudeProps,
      index,
      count,
    })

    router.push(createSignInUrl(`/doctors/${doctorInfo.id}`, locale))
  }

  useEffect(() => {
    setCommonAmplitudeProps({
      doctor_id: doctorInfo.id,
      doctor_name: doctorInfo.name,
      hospital_id: doctorInfo.hospitalId,
      hospital_name: doctorInfo.hospitalName,
      referrer: document.referrer,
    })

    if (community.comments.content.length === 0 && community.documents.content.length > 0) {
      setCommunityFilter(DoctorCommunityActivityFilter.POST)
    }
  }, [community, doctorInfo])

  return (
    <>
      <section className="px-6 py-2">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-semibold">{t('pages.doctorDetail.activitiesInCommunity')}</h2>
          {userId !== NONMEMBER_ID && (
            <Link
              aria-label="show all communities"
              href={`/doctors/${doctorInfo.id}/community?filter=${communityFilter}`}
              onClick={() => postAmplitude('click_activity_more_in_doctor_detail', commonAmplitudeProps)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 cursor-pointer no-underline transition-colors"
            >
              {t('pages.doctorDetail.viewAll')}
              <IcArrowRight width={12} height={12} />
            </Link>
          )}
        </div>

        <div className="flex gap-2 mb-4">
          {community.comments.content.length > 0 && (
            <button
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-all duration-200 ${
                communityFilter === DoctorCommunityActivityFilter.REPLY
                  ? 'bg-orange-50 border-orange-500 text-orange-500 shadow-sm'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
              aria-label="reply filter button"
              onClick={() => {
                setCommunityFilter(DoctorCommunityActivityFilter.REPLY)
                postAmplitude('click_activity_pillbtn_in_doctor_detail', {
                  ...commonAmplitudeProps,
                  index: 'comment',
                  count: community?.comments.totalElements,
                })
              }}
            >
              <span className="font-medium">{t('pages.doctorDetail.reply')}</span>
              <span
                className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                  communityFilter === DoctorCommunityActivityFilter.REPLY
                    ? 'bg-orange-100 text-orange-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {community.comments.totalElements}
              </span>
            </button>
          )}
          {community.documents.content.length > 0 && (
            <button
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-all duration-200 ${
                communityFilter === DoctorCommunityActivityFilter.POST
                  ? 'bg-orange-50 border-orange-500 text-orange-500 shadow-sm'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
              aria-label="post filter button"
              onClick={() => {
                setCommunityFilter(DoctorCommunityActivityFilter.POST)
                postAmplitude('click_activity_pillbtn_in_doctor_detail', {
                  ...commonAmplitudeProps,
                  index: 'document',
                  count: community?.documents.totalElements,
                })
              }}
            >
              <span className="font-medium">{t('pages.doctorDetail.post')}</span>
              <span
                className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                  communityFilter === DoctorCommunityActivityFilter.POST
                    ? 'bg-orange-100 text-orange-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {community.documents.totalElements}
              </span>
            </button>
          )}
        </div>
        <div
          key={communityFilter}
          ref={communityCardsWrapperRef}
          role="presentation"
          onMouseMove={handleCommunityCardsWrapperMouseMove}
          onMouseDown={(e) => {
            e.preventDefault()
            setIsCommunityDragging(true)
          }}
          onMouseUp={() => setIsCommunityDragging(false)}
          onMouseLeave={() => setIsCommunityDragging(false)}
          className="flex px-6 -mx-6 gap-4 overflow-x-auto overflow-y-hidden scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {communityFilter === DoctorCommunityActivityFilter.REPLY &&
            community.comments.content
              .slice(0, MAXIMUM_COMMUNITY_ACTIVITIES_COUNT)
              .map((comment, commentIndex) => (
                <CommentCard
                  key={comment.comment.id}
                  comment={comment}
                  commentIndex={commentIndex}
                  userId={userId}
                  commonAmplitudeProps={commonAmplitudeProps}
                  onClickLoginAndWatch={onClickLoginAndWatch}
                />
              ))}
          {communityFilter === DoctorCommunityActivityFilter.POST &&
            community.documents.content
              .slice(0, MAXIMUM_COMMUNITY_ACTIVITIES_COUNT)
              .map((document, documentIndex) => (
                <DocumentCard
                  key={document.communityId}
                  document={document}
                  documentIndex={documentIndex}
                  userId={userId}
                  commonAmplitudeProps={commonAmplitudeProps}
                  onClickLoginAndWatch={onClickLoginAndWatch}
                />
              ))}
          {userId !== NONMEMBER_ID &&
            communityFilter === DoctorCommunityActivityFilter.REPLY &&
            community.comments.totalElements > MAXIMUM_COMMUNITY_ACTIVITIES_COUNT && (
              <ShowAllCard
                href={`/doctors/${doctorInfo.id}/community?filter=reply`}
                label={t('pages.doctorDetail.showAllReplies')}
                onClick={() => postAmplitude('click_activity_more_in_doctor_detail', commonAmplitudeProps)}
              />
            )}
          {userId !== NONMEMBER_ID &&
            communityFilter === DoctorCommunityActivityFilter.POST &&
            community.documents.totalElements > MAXIMUM_COMMUNITY_ACTIVITIES_COUNT && (
              <ShowAllCard
                href={`/doctors/${doctorInfo.id}/community?filter=post`}
                label={t('pages.doctorDetail.showAllPosts')}
                onClick={() => postAmplitude('click_activity_more_in_doctor_detail', commonAmplitudeProps)}
              />
            )}
        </div>
      </section>
      <div className="my-8 h-px bg-gray-200" />
    </>
  )
}
